import { lockFocus } from '../../utils/focus-lock'
import { disableScroll } from 'application/utils/helpers'
import { ModalConfig } from './config'
import type { ModalStack } from './stack'
import type { ModalListeners } from './listeners'
import type { ICloseModalOptions } from './types'

/**
 * Класс для управления открытием модальных окон.
 */
export class ModalOpener {
  /**
   * Открывает модальное окно с указанным именем.
   * @param {string} modalName - Имя модального окна.
   * @param {MouseEvent | null | undefined} event - Событие клика.
   * @param {ModalStack} modalStack - Стек модальных окон.
   * @param {ModalListeners} listeners - Слушатели событий.
   * @param {Function} closeModalFn - Функция закрытия модального окна.
   */
  public static open(
    modalName: string,
    event: MouseEvent | null | undefined,
    modalStack: ModalStack,
    listeners: ModalListeners,
    closeModalFn: (_options: ICloseModalOptions) => void
  ): void {
    if (!ModalConfig.has(modalName)) {
      // eslint-disable-next-line no-console
      console.warn(`Modal with name "${modalName}" is not configured.`)
      return
    }

    const modal = document.querySelector(
      `[data-modal-name="${modalName}"]`
    ) as HTMLElement

    if (!modal || modal.classList.contains('is-active')) {
      return
    }

    modalStack.setCurrent(modalName)
    const config = ModalConfig.get(modalName)

    if (!config) {
      return
    }

    const openedModal = document.querySelector(
      '.modal.is-active'
    ) as HTMLElement
    if (!config.openOver && openedModal && openedModal.dataset.modalName) {
      closeModalFn({
        modalName: openedModal.dataset.modalName,
        clearStack: false,
        checkModalBack: false
      })
    }

    if (config.openOver) {
      modal.style.zIndex = '1100'
    }

    modal.classList.add('is-active')
    disableScroll()

    config.openCallback?.(event || null)

    if (config.lockFocus) {
      lockFocus(modal, config.startFocus)
    }

    const modalBackBtn = modal.querySelector(
      '[data-close-modal="back"]'
    ) as HTMLButtonElement

    if (modalBackBtn) {
      modalBackBtn.style.display = modalStack.getLength() ? '' : 'none'
    }

    modalStack.push(modalName)

    if (config.resetScrollPos) {
      window.scrollTo(0, 0)
    }

    setTimeout(() => {
      listeners.addModalListeners(modal, config.openOver)
    }, config.eventTimeout)
  }
}
